import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArchiveXIcon, CheckIcon, SproutIcon, BoxIcon } from "lucide-react";
import { loanHeader, recoveryPaths } from "../../data/withdraw";
import { RecoveryPath } from "../../types/withdraw";
import { popSpring } from "../../utils/styles";
interface PathChoiceStepProps {
  value: RecoveryPath | null;
  onChange: (path: RecoveryPath) => void;
}
const tones: Record<RecoveryPath, {
  icon: BoxIcon;
  tile: string;
  tag: string;
  selected: string;
  dot: string;
}> = {
  dead: {
    icon: ArchiveXIcon,
    tile: 'bg-coral-50 text-coral-600',
    tag: 'bg-coral-50 text-coral-700 ring-coral-100',
    selected: 'border-coral-500 bg-coral-50/40 ring-4 ring-coral-100 shadow-lift',
    dot: 'border-coral-600 bg-coral-600'
  },
  seasoning: {
    icon: SproutIcon,
    tile: 'bg-primary-50 text-primary-700',
    tag: 'bg-primary-50 text-primary-800 ring-primary-100',
    selected: 'border-primary-600 bg-primary-50/40 ring-4 ring-primary-100 shadow-lift',
    dot: 'border-primary-700 bg-primary-700'
  }
};
export function PathChoiceStep({
  value,
  onChange
}: PathChoiceStepProps) {
  const reduce = useReducedMotion();
  const firstName = loanHeader.borrower.split(' ')[0];
  return <div data-id="element-113" data-mp="2autp4uc7wci4">
      <h3 className="text-lg font-bold tracking-tight text-slate-900" data-id="element-114" data-mp="26vkyhych4uqs">Where does this loan go from here?</h3>
      <p className="mt-1 text-sm text-slate-500" data-id="element-115" data-mp="21w86kz6qwxhc">
        Pick the path that fits where {firstName} is today. Seasoned loans can be reactivated anytime.
      </p>

      <div role="radiogroup" aria-label="Recovery path" className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-116" data-mp="1wzhuemy2am17">
        {recoveryPaths.map(option => {
        const tone = tones[option.id];
        const Icon = tone.icon;
        const selected = value === option.id;
        return <motion.button key={option.id} type="button" role="radio" aria-checked={selected} onClick={() => onChange(option.id)} whileHover={reduce || selected ? undefined : {
          y: -3
        }} animate={{
          scale: selected ? 1.02 : 1
        }} transition={popSpring} className={`flex h-full flex-col rounded-2xl border p-5 text-left transition-[box-shadow,border-color,background-color] duration-200 ease-out-soft focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-200 ${selected ? tone.selected : 'border-slate-200 bg-white shadow-soft hover:border-slate-300 hover:shadow-lift'}`} data-id="element-117" data-mp="3mbbj8nsce31l">
              <div className="flex items-start justify-between" data-id="element-118" data-mp="1yfdgfr5tm0k3">
                <span className={`grid h-12 w-12 place-items-center rounded-2xl ${tone.tile}`} data-id="element-119" data-mp="23cm1q0m9mkpk">
                  <Icon className="h-6 w-6" aria-hidden="true" data-id="element-120" data-mp="1pcuhvj92ia3n" />
                </span>
                <span className={`grid h-6 w-6 place-items-center rounded-full border-2 transition-colors duration-150 ${selected ? tone.dot : 'border-slate-300 bg-white'}`} aria-hidden="true" data-id="element-121" data-mp="1uch3oh2yoyo2">
                  <AnimatePresence data-id="element-122" data-mp="iyd3gt5s6eak">
                    {selected && <motion.span initial={{
                  scale: 0.5,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} exit={{
                  scale: 0.5,
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} data-id="element-123" data-mp="iz4zu677cg1w">
                        <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} data-id="element-124" data-mp="fgxq2uwiyvfc" />
                      </motion.span>}
                  </AnimatePresence>
                </span>
              </div>

              <div className="mt-5 flex items-center gap-2" data-id="element-125" data-mp="3ixo1xqesg2oy">
                <span className="text-lg font-bold tracking-tight text-slate-900" data-id="element-126" data-mp="1g4us7yefwb6">{option.title}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone.tag}`} data-id="element-127" data-mp="3susnbo0t7u4p">{option.tag}</span>
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600" data-id="element-128" data-mp="353x0z6f4hr4u">{option.description}</p>

              <div className="mt-auto flex flex-wrap gap-2 pt-5" data-id="element-129" data-mp="305c2bbfirfii">
                {option.meta.map(m => <span key={m} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600" data-id="element-130" data-mp="23ft2og4snykj">
                    {m}
                  </span>)}
              </div>
            </motion.button>;
      })}
      </div>
    </div>;
}